// @ts-nocheck
import { Point } from "./Point";
import { isZeroNegative } from "../shared/common";

export class Vector {
    private _start: Point;
    private _end: Point;
    xc: number;
    yc: number;
    length: number;
    nx: number;
    ny: number;

    get start(): Point {
        return this._start;
    }
    set start(point) {
        this._start = this.clonePointLike(point);
        this.xc = this._end.x - this._start.x;
        this.yc = this._end.y - this._start.y;  
    }
    get end(): Point {
        return this._end;
    }
    set end(point) {
        this._end = this.clonePointLike(point);
        this.xc = this._end.x - this._start.x;
        this.yc = this._end.y - this._start.y;
    }

    static getVec0() {
        return new Vector(new Point(100, 0), new Point(200, 0));
    }


    private clonePointLike(p: any): Point {
        if (p instanceof Point && typeof (p as any).clone === "function") {
            return (p as Point).clone();
        }
        if (p && typeof p.x === "number" && typeof p.y === "number") {
            return new Point(p.x, p.y);
        }
        // Fallback to origin to avoid runtime crashes; caller logic should prevent this.
        return Point.ORIGIN.clone();
    }


    constructor(start: any, end: any) {

        this._start = this.clonePointLike(start);
        this._end = this.clonePointLike(end);
        this.xc = this._end.x - this._start.x;
        this.yc = this._end.y - this._start.y;

        this.length = Math.hypot(this.xc, this.yc);
        this.nx = this.yc / this.length;
        this.ny = -this.xc / this.length;
    }

    getSkewProduct(vec) {
        var skew = this.xc * vec.yc - this.yc * vec.xc;
        return skew;
    }

    dotProduct(vec) {
        var skew = this.getSkewProduct(vec);
        var dotProduct;

        if (skew === 0) {
            if (isZeroNegative(skew)) {
                dotProduct = -(this.xc * vec.xc + this.yc * vec.yc);
            }
            else {
                dotProduct = this.xc * vec.xc + this.yc * vec.yc;
            }
        }
        else if (skew > 0) {
            dotProduct = this.xc * vec.xc + this.yc * vec.yc;
        }
        else if (skew < 0) {
            dotProduct = -(this.xc * vec.xc + this.yc * vec.yc);
        }
        return dotProduct;
    };

    areVectorsInSameDirection(vec) {

        // Проверяем угол между векторами

        var dotProduct = this.dotProduct(vec);

        const angle = Math.acos(dotProduct / (this.length * vec.length));

        // Если угол меньше π/2, то векторы направлены в одну сторону
        return angle <= Math.PI / 2;
    }

    getPerpendicularVector() {
        let dx = this._end?.x - this._start?.x;
        let dy = this._end?.y - this._start?.y;

        // Calculate a perpendicular vector by swapping and negating components
        let perpendicularVector = new Vector(
            new Point(this._start.x, this._start.y),
            new Point(this._start.x + dy, this._start.y - dx)

        );

        return perpendicularVector;
    }

    getPerpendicularVectorAtPoint(point) {
        let dx = this._end?.x - this._start?.x;
        let dy = this._end?.y - this._start?.y;

        let perpendicularVector = new Vector(
            new Point(point.x, point.y),
            new Point(point.x + dy, point.y - dx)
        );

        return perpendicularVector;
    }

    getOppositeToPerpendicularVectorAtPoint(point) {
        // Calculate the components of the perpendicular vector
        let dx = this._end?.x - this._start?.x;
        let dy = this._end?.y - this._start?.y;

        // Create the opposite of the perpendicular vector
        let oppositeToPerpendicularVector = new Vector(
            new Point(point.x, point.y),   // Start point remains the same
            new Point(point.x - dy, point.y + dx)  // Opposite of the perpendicular vector
        );

        return oppositeToPerpendicularVector;
    }
    

    


    getOrtho1X() {
        return new Vector(
            new Point(this._start.x, this._end.y),
            new Point(this._end.x, this._end.y)
        );
    }
    getOrtho2X() {
        return new Vector(
            new Point(this._start.x, this._start.y),
            new Point(this._end.x, this._start.y)
        );

    }

    getOrtho1Y() {
        return new Vector(
            new Point(this._end.x, this._start.y),
            new Point(this._end.x, this._end.y)
        );
    }
    getOrtho2Y() {
        return new Vector(
            new Point(this._start.x, this._start.y),
            new Point(this._start.x, this._end.y)
        );
    }

    getAngleRad(vec) {

        let dotProduct = this.dotProduct(vec);

        let angleRad = Math.acos(dotProduct / (this.length * vec.length));

        return angleRad;
    }
    getAngleDeg(vec) {
        let angleRad = this.getAngleRad(vec);
        let angleDeg = angleRad * (180 / Math.PI);
        
        return angleDeg;
    }

    createParallelVectorAtPoint(point) {
        let dx = this._end.x - this._start.x;
        let dy = this._end.y - this._start.y;

        // Calculate the coordinates of the new end point for the parallel vector
        let newEndX = point.x + dx;
        let newEndY = point.y + dy;

        // Create a new parallel vector with the given point and the calculated end point
        let parallelVector = new Vector(
            new Point(point.x, point.y),
            new Point(newEndX, newEndY)
        );

        return parallelVector;
    }

    createVectorAtAngle(angleDeg) {
        // Convert angle from degrees to radians
        var angleRad = (angleDeg * Math.PI) / 180;
        
        // Calculate the new coordinates
        var newX = this.xc * Math.cos(angleRad) - this.yc * Math.sin(angleRad);
        var newY = this.xc * Math.sin(angleRad) + this.yc * Math.cos(angleRad);


        var endPoint = new Point(
            this.start.x + newX,
            this.start.y - newY
        );

        // Create and return the new vector
        return new Vector(this.start, endPoint);
    }
    

}

// Ensure Vector is exported as both named and default export for compatibility
export default Vector;